import React, { Component } from 'react';

import { inject, observer } from 'mobx-react';

import { Card, Table, Modal, Button, Spin, Popover } from 'antd';

import moment from 'moment';
import { api } from '../../../../../services';
import { pick } from 'lodash';

import './AlarmsCard.css';

const columns = (translate, longDateFormat) => [
  {
    title: translate('carView.alarms.table.columns.description'),
    dataIndex: 'description',
    render: (text, record) => (translate('carView.alarms.descriptions.' + record.code.toLowerCase()))
  },
  {
    title: translate('carView.alarms.table.columns.initialRecordedAt'),
    dataIndex: 'initialRecordedAt',
    render: text => <div>{moment(text).format(longDateFormat.L)}</div>,
    width: '100px'
  },
  {
    title: translate('carView.alarms.table.columns.initialOdometer'),
    align: 'center',
    dataIndex: 'initialOdometer',
    width: '70px'
  },
  {
    title: translate('carView.alarms.table.columns.km'),
    align: 'center',
    dataIndex: 'km',
    width: '70px'
  }
];

const serviceLevelsWithLimitedFeatures = ['light-a'];

@inject('translationsStore', 'commonStore')
@observer
export default class AlarmsCard extends Component {
  constructor(props) {
    super(props);
    const { commonStore, translationsStore } = props;
    
    this.historyTableColumns = [
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.code'),
        dataIndex: 'code',
        sorter: true,
        sortOrder: false,
        width: 120,
        className: 'code',
        render: (text, record) => <span className={record.severity === 'red' ? 'red' : 'orange'}>{text}</span>,
      },
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.description'),
        dataIndex: 'description',
        sorter: true,
        sortOrder: false,
        render: (text, record) => translationsStore.translate('carView.alarms.descriptions.' + record.code.toLowerCase()),
      },
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.resolvedAt'),
        dataIndex: 'resolvedAt',
        sorter: true,
        sortOrder: false,
        width: 120,
        align: 'center',
        render: text => moment(text).format(commonStore.longDateFormat.L),
      },
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.recordedAt'),
        dataIndex: 'recordedAt',
        sorter: true,
        sortOrder: false,
        width: 120,
        align: 'center',
        render: text => moment(text).format(commonStore.longDateFormat.L),
      },
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.initialOdometer'),
        dataIndex: 'initialOdometer',
        sorter: true,
        sortOrder: false,
        width: 100,
        align: 'center',
        render: text => new Intl.NumberFormat(commonStore.dateFnsLocale).format(text),
      },
      {
        title: translationsStore.translate('carView.alarms.history.table.columns.km'),
        dataIndex: 'km',
        sorter: true,
        sortOrder: false,
        width: 100,
        align: 'center',
        render: text => new Intl.NumberFormat(commonStore.dateFnsLocale).format(text),
      },
    ];

    this.historyFetchMethods = [
      {
        key: 'alarms',
        method: 'getAlarmsHistory',
        defaultSorter: { field: 'resolvedAt', order: 'DESC' }
      }
    ];

    this.state = {
      sorters: { alarms: this.historyFetchMethods[0].defaultSorter }
    };
  }

  loadHistory = (methods = ['alarms'], page = 1) => {
    const { carId } = this.props;
    
    // Guard: don't load if carId is not available
    if (!carId) {
      console.error('AlarmsCard: Cannot load history without carId');
      return;
    }
    
    this.setState({
      historyLoading: true
    });
    const { sorters } = this.state;
    Promise.all(
      methods.map(method => {
        const { field: sort, order } = sorters[method];
        return api(this.historyFetchMethods.find(helper => helper.key === method).method, carId, page, sort, order);
      })
    ).then(methodsResponse => {
      const historySum = !!this.state.historySum
        ? this.state.historySum
        : methods.reduce((accumulator, method, index) => {
            return accumulator + methodsResponse[index].count;
          }, 0);
      let computedResources = methodsResponse.map((response, index) => ({
        key: methods[index],
        count: response.count,
        page,
        dataSource: response.rows.map((row, index) => ({
          ...row,
          key: index
        })),
        pagination: { total: response.count, current: page }
      }));
      let definedResources = this.state.historyResources;
      if (definedResources) {
        definedResources = definedResources.map(item => {
          const resource = computedResources.find(resource => resource.key === item.key);
          return resource ? resource : item;
        });
      } else {
        definedResources = computedResources;
      }

      const historyActiveKey = definedResources.reduce((accumulator, currentValue) => {
        return accumulator === null && methods.findIndex(method => method === currentValue.key) > -1 && currentValue.count > 0 ? currentValue.key : accumulator;
      }, null);

      let historyData = {
        historyLoading: false,
        historySum: historySum,
        historyResources: definedResources,
        historyActiveKey
      };

      if (historySum !== 0) {
        historyData = { ...historyData, historyShow: true, historyNoDataHint: false };
      } else {
        historyData = { ...historyData, historyShow: false, historyNoDataHint: true };
      }
      this.setState(historyData);
    });
  };

  handleTableChange = (key, pagination, filters, sorter) => {
    const { current: page } = pagination;
    const sorterVal = pick(sorter, ['field', 'order']);
    sorterVal.order = sorterVal.order === 'descend' ? 'DESC' : 'ASC';
    this.setState({ sorters: { ...this.state.sorters, [key]: sorterVal } }, () => {
      this.loadHistory([key], page);
    });
  };

  render() {
    const { translationsStore, commonStore, alarms, otheralarms } = this.props;
    const { disabled } = this.props;
    const { sorters, historyActiveKey } = this.state;
    const isDisabled = disabled || serviceLevelsWithLimitedFeatures.includes(this.props.serviceLevel);
    
    const historyColumns = this.historyTableColumns.map(column => {
      let sortOrder = (sorters[historyActiveKey] || {}).field === column.dataIndex ? sorters[historyActiveKey].order : false;
      sortOrder = sortOrder === 'DESC' ? 'descend' : sortOrder === 'ASC' ? 'ascend' : false;
      return {
        ...column,
        sortOrder
      };
    });

    return (
      <Card
        className="AlarmsCard"
        style={{
          ...this.props.style,
          opacity: isDisabled ? 0.5 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
        id="AlarmsCard"
      >
        <div className="header">
          <div>{translationsStore.translate('carView.alarms.title')}</div>
          {!isDisabled && (
            <Popover
              content={translationsStore.translate('carView.alarms.historyNoData.message')}
              title={translationsStore.translate('carView.alarms.historyNoData.title')}
              trigger="click"
              visible={this.state.historySum === 0 && this.state.historyNoDataHint}
              onVisibleChange={visible => this.setState({ historyNoDataHint: visible })}
            >
              <Button type="primary" onClick={() => this.loadHistory()}>
                {translationsStore.translate('carView.history')}
              </Button>
            </Popover>
          )}
        </div>
        <div className="container" style={{ position: 'relative' }}>
          {isDisabled && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.6)',
                zIndex: 2,
                cursor: 'not-allowed',
              }}
            />
          )}
          <Table
            rowClassName={record => (record.safety === true ? 'safety' : '')}
            pagination={false}
            columns={columns(translationsStore.translate.bind(translationsStore), commonStore.longDateFormat)}
            dataSource={alarms}
            rowKey={record => record.id}
          />
          {!!this.props.otheralarms && this.props.otheralarms.length > 0 && !isDisabled && (
            <div
              onClick={() => this.setState({ showothers: true })}
              style={{
                marginTop: '10px',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 'bold',
              }}
            >
              {this.props.otheralarms.length} {translationsStore.translate('carView.alarms.otheralarms.message')}
            </div>
          )}
        </div>
        <Modal
          rowClassName={record => (record.safety === true ? 'safety' : '')}
          title={translationsStore.translate('carView.alarms.otheralarms.title')}
          width="calc(var(--tablet-width) / var(--modal-width-ratio))"
          visible={this.state.showothers}
          getContainer={() => document.getElementById('AlarmsCard')}
          footer={null}
          onCancel={() => this.setState({ showothers: false })}
          centered={true}
        >
          <Table
            pagination={false}
            columns={columns(translationsStore.translate.bind(translationsStore), commonStore.longDateFormat)}
            dataSource={otheralarms}
            rowKey={record => record.id}
          />
        </Modal>
        <Modal
          title={translationsStore.translate('carView.alarms.history.title')}
          width="calc(var(--tablet-width) / var(--modal-width-ratio))"
          visible={this.state.historyShow}
          getContainer={() => document.getElementById('AlarmsCard')}
          footer={null}
          onCancel={() => this.setState({ historyShow: false })}
          centered={true}
        >
          <Spin spinning={this.state.historyLoading}>
            {(this.state.historyResources || []).map(
              (item, index) =>
                item.count > 0 &&
                item.key === this.state.historyActiveKey && (
                  <Table
                    key={index}
                    dataSource={this.state.historyResources[index] && this.state.historyResources[index].dataSource}
                    columns={historyColumns}
                    pagination={item.pagination}
                    onChange={(...params) => this.handleTableChange(item.key, ...params)}
                    rowKey={record => record.id}
                    scroll={{ x: 'max-content' }}
                  />
                )
            )}
          </Spin>
        </Modal>
      </Card>
    );
  }
}
